import React, { useMemo, useContext } from 'react';
import { Box, Stack, TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, Alert, Link, InputLabel, MenuItem, FormControl, Select, Slide } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { red } from '@mui/material/colors';
import { useDropzone } from 'react-dropzone';
import { DocumentAPIUrl } from '../../utils/extensions/functionEx';
import { HomeContext } from '../../utils/contexts/homeContext';
import { useCookies } from 'react-cookie';

const thumbsContainer = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16
};

const thumb = {
    display: 'inline-flex',
    borderRadius: 2,
    border: '1px solid #eaeaea',
    marginBottom: 8,
    marginRight: 8,
    padding: 4,
    boxSizing: 'border-box'
};

const thumbInner = {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden'
};

const img = {
    display: 'block',
    height: '100px',
    width: '125px'
};

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out'
};

const focusedStyle = {
    borderColor: '#2196f3'
};

const acceptStyle = {
    borderColor: '#00e676'
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function fileSizeValidator(file) {
    const tempSizeMB = (file.size / 1024) / 1024;
    if (tempSizeMB > 7) {
        return {
            code: "file-size-too-large",
            message: `File size is too large, not more than 5 MB.`
        };
    }

    return null
}

const FileUpload = (props) => {
    const { handleUploadFile } = props;
    const { ShowAPIError, OpenErrorAlert, homeState, ClearLoggedUser } = useContext(HomeContext);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    const { getRootProps, getInputProps, isFocused, isDragAccept, fileRejections } = useDropzone({
        disabled: false,
        noKeyboard: true,
        maxFiles: 1,
        accept: {
            'image/*': ['.jpg', '.jpeg', '.png'],
            'application/pdf': ['.pdf'],
        },
        validator: fileSizeValidator,
        onDrop: async (acceptedFiles) => {
            try {
                const formdata = new FormData();
                formdata.append('file', acceptedFiles[0]);

                const headers = new Headers();
                headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

                const response = await fetch(DocumentAPIUrl(homeState.loggedUser.companyCode), {
                    method: "POST",
                    headers: headers,
                    body: formdata
                });
                if (response.status === 401) {
                    OpenErrorAlert('User login history expired, please log in again.');
                    setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                    ClearLoggedUser();
                } else if (response.status === 201) {
                    handleUploadFile(await response.json());
                } else {
                    ShowAPIError(response);
                }
            }
            catch (error) {
                OpenErrorAlert(error)
            }
        }
    });

    const fileRejectionItems = fileRejections.map(({ file, errors }) => (
        errors && errors.length > 0 && <Alert severity="error">{errors[0].message}</Alert>
    ));

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {})
    }), [
        isFocused,
        isDragAccept
    ]);


    return (
        <section className="container">
            <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop image / pdf here, or click to select image / pdf</p>
            </div>
            <aside style={thumbsContainer}>
                {fileRejectionItems}
            </aside>
        </section>
    );
}


const ProfilePhotoUpload = (props) => {
    const { handleUploadFile } = props;
    const { ShowAPIError, OpenErrorAlert, ClearLoggedUser } = useContext(HomeContext);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    const { getRootProps, getInputProps, isFocused, isDragAccept, fileRejections } = useDropzone({
        disabled: false,
        noKeyboard: true,
        maxFiles: 1,
        accept: {
            'image/*': ['.jpg', '.jpeg', '.png']
        },
        validator: fileSizeValidator,
        onDrop: async (acceptedFiles) => {
            try {
                const formdata = new FormData();
                formdata.append('file', acceptedFiles[0]);

                const headers = new Headers();
                headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

                const response = await fetch(DocumentAPIUrl('a1'), {
                    method: "POST",
                    headers: headers,
                    body: formdata
                });

                if (response.status === 401) {
                    OpenErrorAlert('User login history expired, please log in again.');
                    setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                    ClearLoggedUser();
                } else if (response.status === 201) {
                    const tempProfilePic = Object.assign(acceptedFiles[0], {
                        preview: URL.createObjectURL(acceptedFiles[0])
                    })
                    const tempProfilePhoto = { ...await response.json(), profile_doc: tempProfilePic };
                    handleUploadFile(tempProfilePhoto);
                } else {
                    ShowAPIError(response);
                }
            }
            catch (error) {
                OpenErrorAlert(error)
            }
        }
    });

    const fileRejectionItems = fileRejections.map(({ file, errors }) => (
        errors && errors.length > 0 && <Alert severity="error">{errors[0].message}</Alert>
    ));

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {})
    }), [
        isFocused,
        isDragAccept
    ]);


    return (
        <section className="container">
            <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop image here, or click to select image</p>
            </div>
            <aside style={thumbsContainer}>
                {fileRejectionItems}
            </aside>
        </section>
    );
}

const DocumentForm = (props) => {
    const { isOpen, isProfilePhoto, isAadhaarDoc, handleSaveClick, handleCancelClick } = props;

    const [uploadedFile, setUploadedFile] = React.useState(null);
    const [documentType, setDocumentType] = React.useState(isProfilePhoto ? 'Profile Photo' : (isAadhaarDoc ? 'Aadhaar' : ''));
    const [docNo, setDocNo] = React.useState('');
    const [notes, setNotes] = React.useState('');
    const [isSaveButtonEnabled, setIsSaveButtonEnabled] = React.useState(false);

    React.useEffect(() => {
        setIsSaveButtonEnabled((documentType && documentType.trim().length > 0) && ((docNo && docNo.trim().length > 0) || uploadedFile) ? true : false);
    }, [docNo, uploadedFile, documentType]);

    const handleUploadFile = async (tempFile) => {
        setUploadedFile(tempFile);
    }

    const handleSaveButtonClick = async () => {
        const docFormValue = {
            id: 0,
            docNo: docNo.trim(),
            notes: notes.trim(),
            uploadedFileId: uploadedFile ? uploadedFile.id : 0,
            fileName: uploadedFile ? uploadedFile.doc_name : '',
            documentType: documentType,
            doc_data: isProfilePhoto && uploadedFile ? uploadedFile.profile_doc : null
        };
        handleSaveClick(docFormValue);
    }

    const handleDeleteClick = () => {
        setUploadedFile(null);
    }

    return (
        <React.Fragment>
            <Dialog maxWidth={"sm"} fullWidth
                open={isOpen}
                keepMounted
                onClose={handleCancelClick}
                slotProps={{
                    transition: Transition
                }}
            >
                <DialogTitle>
                    {
                        isProfilePhoto ? "Upload Profile Photo" : "Upload New Document"
                    }
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ paddingTop: '14px' }}>
                        <Stack spacing={2}>
                            {
                                isProfilePhoto &&
                                <TextField size="small" required
                                    label="Document Type"
                                    helperText={documentType.length === 0 ? "Enter valid document type (Voter ID, Driving Licence)" : ""}
                                    value={documentType}
                                    slotProps={{
                                        input: {
                                            readOnly: isProfilePhoto || isAadhaarDoc
                                        },
                                    }}
                                    onChange={(event) => {
                                        setDocumentType(event.target.value);
                                    }}
                                />
                            }
                            {
                                !isProfilePhoto &&
                                <FormControl fullWidth size="small" error={documentType.length === 0 ? true : false}>
                                    <InputLabel id="doc-type-select-label">Document Type</InputLabel>
                                    <Select
                                        labelId="doc-type-select-label"
                                        id="doc-type-select"
                                        value={documentType}
                                        label="Document Type"
                                        onChange={(event) => {
                                            setDocumentType(event.target.value);
                                        }}
                                    >
                                        <MenuItem value="Aadhaar">Aadhaar</MenuItem>
                                        <MenuItem value="DrivingLicence">Driving Licence</MenuItem>
                                        <MenuItem value="RationCard">Ration Card</MenuItem>
                                        <MenuItem value="VoterID">Voter ID</MenuItem>
                                        <MenuItem value="PaymentReceipt">Payment Receipt</MenuItem>
                                        <MenuItem value="SalaryReceipt">Salary Receipt</MenuItem>
                                    </Select>
                                </FormControl>
                            }
                            {
                                !isProfilePhoto &&
                                <TextField size="small"
                                    label="No"
                                    value={docNo}
                                    helperText={docNo.length === 0 ? "Enter valid document number (Voter ID Number, Driving Licence Number)" : ""}
                                    slotProps={{
                                        input: {
                                            readOnly: isProfilePhoto
                                        },
                                    }}
                                    onChange={(event) => {
                                        setDocNo(event.target.value);
                                    }}
                                />
                            }
                            {
                                !isProfilePhoto &&
                                <TextField size="small"
                                    label="Notes"
                                    helperText=""
                                    value={notes}
                                    onChange={(event) => {
                                        setNotes(event.target.value);
                                    }}
                                />
                            }
                            {
                                uploadedFile &&
                                <Stack direction="row" spacing={2}>
                                    <Link href="#" underline="none" target="_blank" rel="noopener" sx={{ pt: 1 }}>
                                        {uploadedFile.doc_name}
                                    </Link>
                                    <IconButton aria-label="delete" onClick={handleDeleteClick}>
                                        <ClearIcon sx={{ color: red[500] }} />
                                    </IconButton>
                                </Stack>
                            }

                            {
                                !isProfilePhoto && !uploadedFile && <FileUpload handleUploadFile={handleUploadFile} />
                            }
                            {
                                isProfilePhoto && !uploadedFile && <ProfilePhotoUpload handleUploadFile={handleUploadFile} />
                            }

                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ paddingRight: "24px" }}>
                    <Button variant="contained" size="small"
                        disabled={!isSaveButtonEnabled}
                        onClick={handleSaveButtonClick}>Save</Button>

                    <Button variant="outlined" size="small" onClick={handleCancelClick}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export default DocumentForm;